import { EventEmitter } from 'events'
import { ITask } from '../../components/task/Task'
import BoardStore from '../boards/BoardStore'
import BoardNotFoundError from '../boards/errors/BoardNotFoundError'
import Dispatcher from '../Dispatcher'
import { IBaseMoveError } from '../errors/BaseMoveError'
import SwimlaneNotFoundError from '../swimlanes/errors/SwimlaneNotFoundError'
import SwimlaneStore from '../swimlanes/SwimlaneStore'
import { TaskMoveError, TaskMoveErrorTypes } from './errors/TaskMoveError'
import TaskNotFoundError from './errors/TaskNotFoundError'
import {
    ADD_TASK,
    DELETE_TASK,
    isTaskMovePayloadAction,
    isTaskPayloadAction,
    MOVE_TASK,
    UPDATE_TASK,
} from './TaskActions'

/** Events associated with adding, editing and deleting tasks */
export type CRUDEventTypes =
    | EventsEnum.ADD
    | EventsEnum.EDIT
    | EventsEnum.DELETE
    | EventsEnum.INSERT
    | EventsEnum.MOVE

/** Represents all possible event values for the task store */
enum EventsEnum {
    ADD = 'TASKSTORE_ADD',
    EDIT = 'TASKSTORE_EDIT',
    DELETE = 'TASKSTORE_DELETE',
    INSERT = 'TASKSTORE_INSERT',
    MOVE = 'TASKSTORE_MOVE',
}

/**
 * Store for all Tasks in the application.
 * Represents an aggregate as all Tasks belong to a Task and board.
 * As such this implementation wraps the board store.
 */
class TaskStore extends EventEmitter {
    private _nextId = 0
    private _dispatchToken: string

    /**
     * Exposes the dispatch token for the task store
     */
    get DispatchToken() {
        return this._dispatchToken
    }

    /**
     * Computes the initial Total value once the board
     * store has loaded.
     */
    private getInitialTotal(): number {
        const ids = BoardStore.getOrderedBoardsIds()
        if (ids.length === 0) return 0
        const swimlanes = ids
            .map((id) => BoardStore.getBoard(id))
            .flatMap((board) => board.swimlanes)
        if (swimlanes.length === 0) return 0
        const tasks = swimlanes.flatMap((swimlanes) => swimlanes.tasks)
        if (tasks.length === 0) return 0
        return (
            tasks.reduce((previous, current) =>
                previous.id > current.id ? previous : current
            ).id + 1
        )
    }

    constructor() {
        super()
        BoardStore.addStateChangeListener(() => {
            if (BoardStore.state !== 'LOADED') return
            this._nextId = this.getInitialTotal()
        })
        this._dispatchToken = Dispatcher.register((action) => {
            // typeguard ensures only IActionPayload<ITaskPayload> types get to the switch
            if (isTaskPayloadAction(action)) {
                switch (action.actionType) {
                    case ADD_TASK:
                        this.addTask(
                            action.payload.boardId,
                            action.payload.swimlaneId,
                            action.payload.Task
                        )
                        break
                    case UPDATE_TASK:
                        this.updateTask(
                            action.payload.boardId,
                            action.payload.swimlaneId,
                            action.payload.Task
                        )
                        break
                    case DELETE_TASK:
                        this.deleteTask(
                            action.payload.boardId,
                            action.payload.swimlaneId,
                            action.payload.Task
                        )
                        break
                    default:
                    // do nothing
                }
            } else if (isTaskMovePayloadAction(action)) {
                if (action.actionType === MOVE_TASK) {
                    const { from, to } = action.payload
                    this.moveTask(
                        from.boardId,
                        from.swimlaneId,
                        from.taskId,
                        to.boardId,
                        to.swimlaneId,
                        to.taskId
                    )
                }
            }
        })
    }

    /**
     * Adds a Task event listener
     * @param event Event to listen to
     * @param callback Callback to invoke when event raised
     */
    addTaskStoreListener(
        event: EventsEnum,
        callback: (boardId: number, swimlaneId: number, taskId: number) => void
    ) {
        this.on(event, callback)
    }

    /**
     * Removes a Task event listener
     * @param event Event to unsubscribe to
     * @param callback Callback to unsubscribe from the event
     */
    removeTaskStoreListener(
        event: EventsEnum,
        callback: (boardId: number, swimlaneId: number, taskId: number) => void
    ) {
        this.removeListener(event, callback)
    }

    /**
     * Adds a Task to a swimlane in the store
     * @param boardId Board with swimlane to add task to
     * @param swimlaneId Swimlane to add task to
     * @param Task Task to add to swimlane
     */
    addTask(boardId: number, swimlaneId: number, task: ITask) {
        // find the swimlane in the store
        const _swimlane = SwimlaneStore.getSwimlane(boardId, swimlaneId)
        // set the Task id
        task.id = this._nextId
        this._nextId += 1
        // add as first in list
        task.order = 0
        // increment order of all other tasks
        _swimlane.tasks.forEach((x) => (x.order += 1))
        // add Task to the swimlane
        _swimlane.tasks = [task, ..._swimlane.tasks]
        SwimlaneStore.setSwimlane(boardId, _swimlane)
        this.emitCRUDEvent(EventsEnum.ADD, boardId, _swimlane.id, task.id)
    }

    /**
     * Updates an existing Task
     * @param boardId Id of the board the swimlane containing the task belongs to
     * @param swimlaneId Id of the swimlane containing the task
     * @param task Task to update
     */
    updateTask(boardId: number, swimlaneId: number, task: ITask) {
        const _swimlane = SwimlaneStore.getSwimlane(boardId, swimlaneId)
        // ensure Task is on swimlane
        const _task = _swimlane.tasks.find((x) => x.id === task.id)
        if (!_task) throw new TaskNotFoundError(boardId, swimlaneId, task.id)
        // strip the order field from the inbound task
        const { order, ..._update } = task
        // update the Task in the swimlane
        const taskIndex = _swimlane.tasks.findIndex((x) => x.id === task.id)
        _swimlane.tasks = [
            ..._swimlane.tasks.slice(0, taskIndex),
            ..._swimlane.tasks.slice(taskIndex + 1),
            { ..._task, ..._update },
        ]
        // order based on order property
        _swimlane.tasks.sort((a, b) => a.order - b.order)
        // update swimlane
        SwimlaneStore.setSwimlane(boardId, _swimlane)
        this.emitCRUDEvent(EventsEnum.EDIT, boardId, _swimlane.id, task.id)
    }

    /**
     * Deletes a Task from the swimlane / board
     * @param boardId Id of the board the swimlane containing the task belongs to
     * @param swimlaneId Id of the swimlane this task belongs to
     * @param task Task to delete
     */
    deleteTask(boardId: number, swimlaneId: number, task: ITask) {
        // find the swimlane with this Task
        const _swimlane = SwimlaneStore.getSwimlane(boardId, swimlaneId)
        // ensure Task is on swimlane
        const _task = _swimlane.tasks.find((x) => x.id === task.id)
        if (!_task) throw new TaskNotFoundError(boardId, swimlaneId, task.id)
        // delete Task
        const taskIndex = _swimlane.tasks.findIndex((x) => x.id === task.id)
        _swimlane.tasks = [
            ..._swimlane.tasks.slice(0, taskIndex),
            ..._swimlane.tasks.slice(taskIndex + 1),
        ]
        // update order by subtracting 1 from every item after the one deleted
        _swimlane.tasks
            .sort((a, b) => a.order - b.order)
            .slice(_task.order)
            .forEach((x) => (x.order -= 1))
        SwimlaneStore.setSwimlane(boardId, _swimlane)
        this.emitCRUDEvent(EventsEnum.DELETE, boardId, _swimlane.id, _task.id)
    }

    /**
     * Moves a task from one swimlane to another.
     * Permits cross board and swimlane moves and inserts at any location
     * @param fromBoardId BoardId where task resides
     * @param fromSwimlaneId SwimlaneId where task resides
     * @param fromTaskId ID of the task
     * @param toBoardId ID of destination board
     * @param toSwimlaneId ID of destination swimlane
     * @param toTaskId ID of Task to insert the fromTask at in the list, if -1 then inserts as last item in swimlane
     */
    moveTask(
        fromBoardId: number,
        fromSwimlaneId: number,
        fromTaskId: number,
        toBoardId: number,
        toSwimlaneId: number,
        toTaskId: number = -1
    ) {
        const errors: IBaseMoveError<
            TaskMoveErrorTypes,
            BoardNotFoundError | SwimlaneNotFoundError | TaskNotFoundError
        >[] = []
        const isMove =
            fromBoardId === toBoardId && fromSwimlaneId === toSwimlaneId
        let fromTask: ITask = {} as ITask
        try {
            fromTask = this.getTask(fromBoardId, fromSwimlaneId, fromTaskId)
        } catch (error) {
            if (error instanceof BoardNotFoundError)
                errors.push({ errorType: TaskMoveErrorTypes.FromBoard, error })
            else if (error instanceof SwimlaneNotFoundError)
                errors.push({
                    errorType: TaskMoveErrorTypes.FromSwimlane,
                    error,
                })
            else if (error instanceof TaskNotFoundError)
                errors.push({ errorType: TaskMoveErrorTypes.FromTask, error })
            else throw error
        }
        let insertAt: number = -1
        try {
            if (toTaskId !== -1)
                insertAt = this.getTask(toBoardId, toSwimlaneId, toTaskId).order
            else {
                const toSwimlane = SwimlaneStore.getSwimlane(
                    toBoardId,
                    toSwimlaneId
                )
                insertAt = toSwimlane.tasks.length
                if (isMove) insertAt -= 1
            }
        } catch (error) {
            if (error instanceof BoardNotFoundError)
                errors.push({ errorType: TaskMoveErrorTypes.ToBoard, error })
            else if (error instanceof SwimlaneNotFoundError)
                errors.push({ errorType: TaskMoveErrorTypes.ToSwimlane, error })
            else if (error instanceof TaskNotFoundError)
                errors.push({ errorType: TaskMoveErrorTypes.ToTask, error })
            else throw error
        }
        // if any errors then throw new task move error
        if (errors.length !== 0) throw new TaskMoveError(errors)
        // beyond this point both task and destination will have been validated

        // get the destination swimlane
        let toSwimlane = SwimlaneStore.getSwimlane(toBoardId, toSwimlaneId)

        // determine if this is a move, or a delete and insert
        if (!isMove) {
            // not a move
            // delete the from task
            this.deleteTask(fromBoardId, fromSwimlaneId, fromTask)
            // set the order on the from task to the insert position
            fromTask.order = insertAt
            // insert the task into the destination swimlane at the desired location
            toSwimlane.tasks.splice(insertAt, 0, fromTask)
            // update order
            toSwimlane.tasks
                .slice(fromTask.order + 1)
                .forEach((x) => (x.order += 1))
        } else {
            // is a move
            //
            // if from.order is smaller than index at then (moving down list):
            // Loop from: from.order + 1 to indexAt + 1, for each iteration subtract 1 from order.
            // e.g. from.order = 1 and indexAt = 6 => loop from 2 to 7, take 1 from order of items
            //
            // if indexAt is smaller than from.order then (moving up list):
            // Loop from: indexAt to from.order, for each iteration add 1 to order
            // e.g. indexAt = 1 and from.order = 6 => loop from 1 to 6, add 1 to order of items
            const [smallest, largest, amount] =
                fromTask.order < insertAt
                    ? [fromTask.order + 1, insertAt + 1, -1]
                    : [insertAt, fromTask.order, 1]
            for (let index = smallest; index < largest; index++)
                toSwimlane.tasks[index].order += amount
            // set the order on the task being moved to the desired position
            const _fromTask = toSwimlane.tasks.find(
                (task) => task.id === fromTaskId
            ) as ITask
            _fromTask.order = insertAt
            // sort the array into order
            toSwimlane.tasks.sort((a, b) => a.order - b.order)
        }
        // update the swimlane
        SwimlaneStore.setSwimlane(toBoardId, toSwimlane)
        // emit insert
        this.emitCRUDEvent(
            isMove ? EventsEnum.MOVE : EventsEnum.INSERT,
            toBoardId,
            toSwimlaneId,
            fromTaskId
        )
    }

    /**
     * Raises event to say the store has changed
     * @param eventType Type of event to raise
     * @param boardId ID of the board the swimlane containing the task belongs to
     * @param swimlaneId ID of the swimlane this task belongs to
     * @param id id of the Task
     */
    emitCRUDEvent(
        eventType: EventsEnum,
        boardId: number,
        swimlaneId: number,
        id: number
    ) {
        this.emit(eventType, boardId, swimlaneId, id)
    }

    /**
     * Gets a Task from a swimlane and returns it
     * @param boardId ID of the board containing the swimlane this task belongs to
     * @param swimlaneId ID of the swimlane the task belongs to
     * @param taskId ID of the Task to fetch
     */
    getTask(boardId: number, swimlaneId: number, taskId: number): ITask {
        const swimlane = SwimlaneStore.getSwimlane(boardId, swimlaneId)
        const task = swimlane.tasks.find((x) => x.id === taskId)
        if (!task) throw new TaskNotFoundError(boardId, swimlaneId, taskId)
        return { ...task }
    }
}

const taskStore = new TaskStore()
export { taskStore as default, EventsEnum }
